import React from "react";
import { useParams } from "react-router-dom";
import ViewPage from "../../components/view/ViewPage";
import ViewFieldCard from "../../components/view/fields/ViewFieldCard";
import ViewTextField from "../../components/view/fields/ViewTextField";
import ViewRichTextField from "../../components/view/fields/ViewRichTextField";
import { Work } from "../../types/work";
import useWorkController from "../../controllers/workController";
import useWorkLogic from "../../hooks/logic/workLogic";
import {
  formatStringArray,
  formatSystemLanguage,
} from "../../common/formatters/workFormatters";
import WorkAncillaryViewSection from "./sections/view/WorkAncillaryViewSection";
import WorkTranslationsViewSection from "./sections/view/WorkTranslationsViewSection";
import WorkVersionsViewSection from "./sections/view/WorkVersionsViewSection";

interface ViewWorkPageProps {
  systemLanguageType: Work["system_language_type"];
}

const ViewWorkPage: React.FC<ViewWorkPageProps> = ({ systemLanguageType }) => {
  const { _id } = useParams<{ _id: string }>();
  const workLogic = useWorkLogic(systemLanguageType);

  if (!_id) {
    throw new Error("Work ID is required for viewing.");
  }

  const controller = useWorkController(_id, systemLanguageType);

  return (
    <ViewPage<Work>
      controller={controller}
      sections={(work) => [
        ...(workLogic.showVersionTable
          ? [<WorkVersionsViewSection key="versions" work={work} />]
          : []),
        ...(workLogic.showWorkTranslationTable
          ? [<WorkTranslationsViewSection key="translations" work={work} />]
          : []),
        ...(workLogic.showAncillaryConnectionSection
          ? [<WorkAncillaryViewSection key="ancillary" work={work} />]
          : []),
      ]}
    >
      <ViewFieldCard title="Basic Information">
        <ViewTextField name="published_id" label="Intellexus ID" />
        <ViewTextField name="name" label="Name" />
        <ViewTextField
          name="system_language_type"
          label="System Language"
          textParser={formatSystemLanguage}
        />
      </ViewFieldCard>

      <ViewFieldCard title="Classification">
        {workLogic.showField("availability") ? (
          <ViewTextField name="availability" label="Availability" />
        ) : null}
        {workLogic.showField("keywords") ? (
          <ViewTextField
            name="keywords"
            label="Keywords"
            textParser={formatStringArray}
          />
        ) : null}
        {workLogic.showField("work_origin") ? (
          <ViewTextField
            name="work_origin"
            label="Origin"
            textParser={formatStringArray}
          />
        ) : null}
        {workLogic.showField("compositional_form") ? (
          <ViewTextField name="compositional_form" label="Compositional Form" />
        ) : null}
      </ViewFieldCard>

      <ViewFieldCard title="Notes">
        <ViewRichTextField name="notes" />
      </ViewFieldCard>
    </ViewPage>
  );
};

export default ViewWorkPage;
